import React, { useState, useEffect } from 'react';
import { useBooks } from '../context/BookContext';
import { ChevronUp, ChevronDown, Check, Trophy, ListOrdered } from 'lucide-react';

const RankingFlow = ({ book, onClose }) => {
  const { books, rankBook, logDuel } = useBooks();
  const [step, setStep] = useState('intro'); // 'intro', 'comparing', 'manual'
  
  // Get existing read books sorted by rank
  const readBooks = books
    .filter(b => b.status === 'read' && b.id !== book.id)
    .sort((a, b) => (a.rank || 999) - (b.rank || 999));

  // Binary Search State
  const [low, setLow] = useState(0);
  const [high, setHigh] = useState(0);
  const [mid, setMid] = useState(0);

  useEffect(() => {
    setHigh(readBooks.length - 1);
    setMid(Math.floor((readBooks.length - 1) / 2));
  }, [readBooks.length]);

  const handleComparison = (likedNewBookMore) => {
    const opponent = readBooks[mid];
    if (opponent) {
      // The Favorites Garden: log this individual duel for aggregation,
      // independent of where the binary search eventually lands the new book.
      logDuel(likedNewBookMore ? book : opponent, likedNewBookMore ? opponent : book);
    }

    let newLow = low;
    let newHigh = high;

    if (likedNewBookMore) {
      newHigh = mid - 1;
    } else {
      newLow = mid + 1;
    }

    if (newLow > newHigh) {
      // Found the spot!
      finishRanking(newLow + 1);
    } else {
      setLow(newLow);
      setHigh(newHigh);
      setMid(Math.floor((newLow + newHigh) / 2));
    }
  };

  const finishRanking = async (finalRank) => {
    await rankBook(book.id, finalRank);
    onClose();
  };

  const startTopThird = () => {
    const topThirdIndex = Math.floor(readBooks.length / 3);
    setLow(0);
    setHigh(topThirdIndex);
    setMid(Math.floor(topThirdIndex / 2));
    setStep('comparing');
  };

  if (step === 'intro') {
    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <h3 style={{ textAlign: 'center' }}>Where does it fit?</h3>
          <div style={cardStyle}>
            <img src={book.cover} alt="" style={miniCover} />
            <div>
              <strong>{book.title}</strong>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#666' }}>{book.author}</p>
            </div>
          </div>

          <div style={optionsGrid}>
            <button onClick={() => setStep('comparing')} style={optionBtn}>
              <ListOrdered size={20} />
              <span>Compare with others</span>
            </button>
            <button onClick={startTopThird} style={optionBtn}>
              <Trophy size={20} color="#ffc107" />
              <span>This is a new favorite!</span>
            </button>
            <button onClick={() => setStep('manual')} style={optionBtn}>
              <ChevronUp size={20} />
              <span>Just place it manually</span>
            </button>
          </div>
          
          <button onClick={onClose} style={cancelBtn}>Maybe later</button>
        </div>
      </div>
    );
  }

  if (step === 'comparing') {
    const opponent = readBooks[mid];
    
    // Fallback if list was empty
    if (!opponent) {
      finishRanking(1);
      return null;
    }

    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <h3 style={{ textAlign: 'center', marginBottom: '2rem' }}>Which did you like more?</h3>
          
          <div style={matchupContainer}>
            <div style={contenderCard} onClick={() => handleComparison(true)}>
              <img src={book.cover} alt="" style={largeCover} />
              <div style={contenderTitle}>{book.title}</div>
            </div>

            <div style={vsLabel}>VS</div>

            <div style={contenderCard} onClick={() => handleComparison(false)}>
              <img src={opponent.cover} alt="" style={largeCover} />
              <div style={contenderTitle}>{opponent.title}</div>
            </div>
          </div>

          <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#999', marginTop: '2rem' }}>
            Tap your favorite of the two
          </p>
        </div>
      </div>
    );
  }

  if (step === 'manual') {
    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <h3>Place in Hall of Fame</h3>
          <p style={{ fontSize: '0.9rem', color: '#666' }}>Pick a position for "{book.title}"</p>
          
          <div style={scrollList}>
            {/* Slot at the very top */}
            <button onClick={() => finishRanking(1)} style={slotBtn}>
              <Check size={16} /> #1 - New Top Favorite
            </button>

            {readBooks.map((b, idx) => (
              <React.Fragment key={b.id}>
                <div style={rankedItem}>
                  <span style={rankNum}>{idx + 1}</span>
                  <span style={itemTitle}>{b.title}</span>
                </div>
                <button onClick={() => finishRanking(idx + 2)} style={slotBtn}>
                  <Check size={16} /> Place here (Rank #{idx + 2})
                </button>
              </React.Fragment>
            ))}
          </div>
          
          <button onClick={() => setStep('intro')} style={cancelBtn}>Back</button>
        </div>
      </div>
    );
  }

  return null;
};

// Styles
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: '1rem' };
const modalStyle = { backgroundColor: 'white', borderRadius: '20px', padding: '1.5rem', width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' };
const cardStyle = { display: 'flex', gap: '12px', alignItems: 'center', backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '12px', marginBottom: '1.5rem' };
const miniCover = { width: '40px', height: '60px', borderRadius: '4px', objectFit: 'cover' };
const optionsGrid = { display: 'flex', flexDirection: 'column', gap: '10px' };
const optionBtn = { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', borderRadius: '12px', border: '1px solid #eee', backgroundColor: 'white', cursor: 'pointer', textAlign: 'left', fontWeight: '600', transition: 'all 0.2s' };
const cancelBtn = { marginTop: '1.5rem', padding: '10px', border: 'none', background: 'none', color: '#999', cursor: 'pointer' };

const matchupContainer = { display: 'flex', alignItems: 'center', gap: '10px' };
const contenderCard = { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', cursor: 'pointer' };
const largeCover = { width: '100%', aspectRatio: '2/3', borderRadius: '8px', objectFit: 'cover', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' };
const contenderTitle = { fontSize: '0.8rem', fontWeight: 'bold', textAlign: 'center', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
const vsLabel = { fontWeight: '900', color: '#ccc', fontSize: '1.2rem' };

const scrollList = { maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '10px' };
const slotBtn = { padding: '8px', border: '1px dashed #4caf50', borderRadius: '8px', backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' };
const rankedItem = { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', opacity: 0.6 };
const rankNum = { fontWeight: 'bold', color: '#999', fontSize: '0.8rem' };
const itemTitle = { fontSize: '0.8rem' };

export default RankingFlow;